import { Avatar, Badge, Button, Icon, Switch, Tooltip } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { CSSProperties, memo, useState } from "react"
import { useTranslation } from "react-i18next"
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer"
import { areEqual, FixedSizeList } from "react-window"

import { useAppDispatch } from "@/App"
import { Table } from "@/components/Table"
import { requestMarkNotificationAsRead } from "@/store/notifications"
import { DataStatus, Notification } from "@/types"

import cls from "./NotificationsList.module.scss"

type NotificationsListProps = {
  notifications: Notification[]
  notificationsStatus: DataStatus | "updating"
  wrapperClassName?: string
  className?: string
  toggleHiddenSections?: () => void
}

export const NotificationsList = ({
  notifications,
  notificationsStatus = "error",
  wrapperClassName,
  className,
  toggleHiddenSections,
}: NotificationsListProps) => {
  const [showUnreadOnly, setShowUnreadOnly] = useState(true)

  const { t } = useTranslation()

  const notificationsToShow = notifications?.filter((notification) => (showUnreadOnly ? !notification.read_at : true))

  return (
    <Table.Card className={wrapperClassName}>
      <Table
        columns="minmax(350px, 1fr) minmax(200px, 250px) 150px 200px minmax(90px, 150px)"
        loading={!notificationsStatus || notificationsStatus === "loading"}
        error={notificationsStatus === "error"}
        empty={!notificationsToShow?.length}
        className={clsx(cls.table, className)}
        minWidth="1100px"
        virtualized
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("notifications:event")}</Table.Th>
            <Table.Th>{t("notifications:team")}</Table.Th>
            <Table.Th className="text-center">{t("notifications:priority")}</Table.Th>
            <Table.Th className={cls.theadOptions}>
              <div className="d-flex">
                <Switch
                  onClick={() => setShowUnreadOnly((current) => !current)}
                  defaultChecked={showUnreadOnly}
                  size="small"
                >
                  {t("notifications:show_unread_only")}
                </Switch>

                {toggleHiddenSections && (
                  <div style={{ marginLeft: "2rem" }}>
                    <Tooltip content={t("switch_view")}>
                      <span>
                        <Button
                          onClick={toggleHiddenSections}
                          iconProps={{ name: "full-screen", size: "small" }}
                          size="small"
                        />
                      </span>
                    </Tooltip>
                  </div>
                )}
              </div>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {notificationsToShow && (
            <AutoSizer>
              {({ height, width }) => (
                <FixedSizeList
                  height={height}
                  width={width}
                  overscanCount={4}
                  itemData={{ notifications: notificationsToShow }}
                  itemCount={notificationsToShow.length}
                  itemKey={(index, data) => data.notifications[index].id}
                  itemSize={100}
                >
                  {NotificationsListRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          )}
        </Table.Tbody>
      </Table>
    </Table.Card>
  )
}

type NotificationsListRowProps = {
  data: {
    notifications: Notification[]
  }
  index: number
  style: CSSProperties
}

const NotificationsListRow = memo(({ data, index, style }: NotificationsListRowProps) => {
  const notification = data.notifications[index]

  return <NotificationsListItem notification={notification} key={notification.id} style={style} />
}, areEqual)

type NotificationsListItemProps = {
  notification: Notification
  style: CSSProperties
}

const NotificationsListItem = ({ notification, style }: NotificationsListItemProps) => {
  const {
    id,
    trackable_id,
    trackable_type,
    recipient_id,
    priority,
    additional_data,
    created_at,
    read_at,
    read_by,
    text,
  } = notification

  const dispatch = useAppDispatch()

  const handleNotificationClick = async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    await dispatch(requestMarkNotificationAsRead(id))

    const queryParams = new URLSearchParams()
    queryParams.append("team_id", String(recipient_id))
    queryParams.append("date", dayjs(created_at).format("YYYY-MM-DD"))

    switch (trackable_type) {
      case "Order":
        {
          queryParams.append("order_id", String(trackable_id))

          const { driver_id } = additional_data
          if (driver_id) {
            queryParams.append("driver_id", String(driver_id))
          }
        }
        break
      case "Driver":
        queryParams.append("driver_id", String(trackable_id))

        break
      default:
    }

    const url = `${window.location.origin}/?${queryParams.toString()}`

    const shouldOpenInNewTab = event.ctrlKey || event.button === 1 // Ctrl+click or middle mouse button
    if (shouldOpenInNewTab) {
      window.open(url)
    } else {
      window.location.href = url
    }
  }

  const creationTime = dayjs(created_at).format("HH:mm")
  const creationRelativeTime = dayjs(created_at).fromNow()

  const { t } = useTranslation()

  return (
    <Table.Tr style={style}>
      <Table.Td>
        <div className={cls.message} onMouseDown={handleNotificationClick}>
          <Avatar iconProps={{ name: "overdues" }} color={getPriorityColor(priority)} className={cls.icon} />
          <span dangerouslySetInnerHTML={{ __html: text }} />
        </div>
      </Table.Td>

      <Table.Td>
        <div className={cls.team}>{additional_data.team_name}</div>
      </Table.Td>

      <Table.Td className="text-center">
        <div className={cls.priority}>
          <Badge size="small" color={getPriorityColor(priority)}>
            {t(`notifications:priorities.${priority}`)}
          </Badge>
        </div>
      </Table.Td>

      <Table.Td className="text-center">
        <div className={cls.time}>
          <div>{creationTime}</div>
          <div className="text-muted">({creationRelativeTime})</div>

          {read_at && (
            <div
              className={cls.readAt}
              title={`Przeczytano o ${dayjs(read_at).format("HH:mm")} ${read_by && `przez (${read_by})`}`}
            >
              <Icon name="check" size="tiny" gutter="right" />
              {`${dayjs(read_at).format("HH:mm")} ${read_by && `(${read_by})`}`}
            </div>
          )}
        </div>
      </Table.Td>

      <Table.Td>
        <div className={cls.actions}>
          <Button
            color="primary"
            variation="contrast"
            iconProps={{ name: "check-circle", size: "large" }}
            onClick={() => dispatch(requestMarkNotificationAsRead(id))}
          />
        </div>
      </Table.Td>
    </Table.Tr>
  )
}

function getPriorityColor(priority: Notification["priority"]) {
  switch (priority) {
    case "high":
      return "alert"
    case "average":
      return "warning"
    case "low":
      return "info"
  }
}
